import React, { FC, useCallback, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { toast } from 'react-toastify';
import { Col, Row } from 'reactstrap';
import { useFeatures } from '../../utils/util/features';
import {
  ENHANCE_DRAFT_DESCRIPTORS,
  FIRST_DRAFT_DESCRIPTORS,
} from '../AI/AssistedQuestionnaire/consts';
import { getAiRequestType, useAiBackgroundTask } from '../AI/common';
import { aiAssistedOutputWrapper } from '../AI/Form/ValidatedInputAIWrapper';
import { OnInputChange } from '../Widgets/Forms/ValidatedForm';
import { ValidatedFormProvider } from '../Widgets/Forms/ValidatedFormContext';
import InputAuxiliary from '../Widgets/Inputs/InputAuxiliary';
import { INPUT_TYPES } from '../Widgets/Inputs/ValidatedInputTypes';

type FeedbackState = any;

interface UseFeedbackAiParams {
  feedback: FeedbackState;
  setFeedback: React.Dispatch<React.SetStateAction<FeedbackState>>;
}

/**
 * Encapsulates all AI banner state, handlers, and feature-flag checks.
 *
 * Returns only what ModalFeedbackEditorButton needs:
 *  - aiFeedbackRecognitionEnabled – gate for rendering
 *  - aiBannerInput – items to spread into each feedback-type inputs array
 *  - feedbackAiFormCustomProperties – props for ValidatedFormProvider
 *  - handleFeedbackFormReady – onReady callback for ModalEditorButton
 *  - resetAiState – call when the modal resets
 */
export function useFeedbackAi({ feedback, setFeedback }: UseFeedbackAiParams) {
  const { formatMessage } = useIntl();
  const { isGroupAndOptionEnabled } = useFeatures();

  // --- feature flags ---
  const aiFeedbackRecognitionEnabled = isGroupAndOptionEnabled(
    'ai_assistant',
    'ai_feedback_recognition'
  );

  const aiCannedResponses = isGroupAndOptionEnabled(
    'ai_assistant',
    'ai_canned_demo_responses'
  );

  // --- internal state ---
  const [aiDraftSuggestions, setAiDraftSuggestions] = useState<
    Record<string, { value: unknown }> | undefined
  >(undefined);

  const [formInputChangeRef, setFormInputChangeRef] = useState<
    React.MutableRefObject<OnInputChange | undefined> | undefined
  >(undefined);

  // --- exposed: onReady callback ---
  const handleFeedbackFormReady = useCallback(
    ({
      onInputChangeRef,
    }: {
      onInputChangeRef: React.MutableRefObject<OnInputChange | undefined>;
      objectRef: React.MutableRefObject<unknown>;
    }) => {
      setFormInputChangeRef(() => onInputChangeRef);
    },
    []
  );

  // --- exposed: ValidatedFormProvider props ---
  const feedbackAiFormCustomProperties = useMemo(
    () => ({
      suggestions: aiDraftSuggestions,
      inputChangeRef: formInputChangeRef,
    }),
    [aiDraftSuggestions, formInputChangeRef]
  );

  // --- exposed: reset helper ---
  const resetAiState = useCallback(() => {
    setAiDraftSuggestions(undefined);
  }, []);

  // --- derived data for the AI request ---
  const hasExistingComments = useMemo(() => {
    const comments =
      typeof feedback?.comments === 'string' ? feedback.comments.trim() : '';
    return comments.length > 0;
  }, [feedback?.comments]);

  const hasNonEmptyDraftContent = useMemo(() => {
    return hasExistingComments || (feedback?.topics?.length ?? 0) > 0;
  }, [hasExistingComments, feedback?.topics]);

  const draftData = useMemo(
    () => ({
      type: feedback?.type,
      comments: feedback?.comments,
      topics: feedback?.topics,
      visibility: feedback?.visibility,
      feedback_visibility: feedback?.feedback_visibility,
      subject_people: feedback?.subject_people,
      requested_people: feedback?.requested_people,
    }),
    [
      feedback?.type,
      feedback?.comments,
      feedback?.topics,
      feedback?.visibility,
      feedback?.feedback_visibility,
      feedback?.subject_people,
      feedback?.requested_people,
    ]
  );

  // --- AI task ---
  const requestParameters = useMemo(
    () => ({
      draft_data: draftData,
      request_type: getAiRequestType(false, aiCannedResponses),
    }),
    [draftData, aiCannedResponses]
  );

  const { requestSuggestion: requestAiDraft } = useAiBackgroundTask({
    suggestionType: 'FEEDBACK_RECOGNITION',
    requestParameters,
  });

  // --- banner callbacks ---
  const handleAiBannerCallback = useCallback(
    (data: any) => {
      const activity = data?.FEEDBACK_RECOGNITION?.response;
      const description = activity?.description ?? '';

      if (!description) {
        toast.warning(
          formatMessage({
            id: 'app.feedback.modal_feedback_editor_button.ai_banner.empty',
            defaultMessage:
              'No draft was generated. Try again with more context.',
          })
        );
        return;
      }

      if (hasExistingComments) {
        const skills = activity?.skills;
        setAiDraftSuggestions({
          comments: { value: description },
          ...(Array.isArray(skills) && skills.length > 0
            ? { topics: { value: skills } }
            : {}),
        });
        return;
      }

      setFeedback((prev) => {
        const updates: Record<string, unknown> = { comments: description };
        const skills = activity?.skills;
        if (Array.isArray(skills) && skills.length > 0) {
          updates.topics = skills;
        }
        return { ...prev, ...updates };
      });
    },
    [formatMessage, hasExistingComments, setFeedback]
  );

  const handleAiBannerSubmit = useCallback(
    (object: any, onCallback: any, handleClose: any, handleError: any) => {
      const userPrompt = (object?.user_prompt ?? '').trim();
      if (!userPrompt) {
        handleError(
          formatMessage(
            hasNonEmptyDraftContent
              ? {
                  id: 'app.feedback.modal_feedback_editor_button.ai_banner.prompt_required_refine',
                  defaultMessage:
                    'Please describe how you want to refine or adjust the draft.',
                }
              : {
                  id: 'app.feedback.modal_feedback_editor_button.ai_banner.prompt_required',
                  defaultMessage:
                    'Please describe what you want the AI to draft.',
                }
          )
        );
        return;
      }
      setAiDraftSuggestions(undefined);
      requestAiDraft(
        { user_prompt: userPrompt },
        onCallback,
        handleClose,
        handleError
      );
    },
    [formatMessage, hasNonEmptyDraftContent, requestAiDraft]
  );

  // --- exposed: banner input items ---
  const aiBannerInputItems = useFeedbackAiBannerInput({
    hasNonEmptyDraftContent,
    handleAiBannerSubmit,
    handleAiBannerCallback,
  });

  const aiBannerInput = useMemo(() => {
    if (!aiFeedbackRecognitionEnabled || !feedback?.type?.heading) {
      return [];
    }
    return aiBannerInputItems;
  }, [
    aiFeedbackRecognitionEnabled,
    feedback?.type?.heading,
    aiBannerInputItems,
  ]);

  return {
    aiFeedbackRecognitionEnabled,
    aiBannerInput,
    feedbackAiFormCustomProperties,
    handleFeedbackFormReady,
    resetAiState,
  };
}

// ---------------------------------------------------------------------------
// FeedbackAiFormWrapper – stable top-level component (not defined inside hook)
// ---------------------------------------------------------------------------

const IDENTITY_WRAPPER = (rawInput: React.ReactNode) => rawInput;

interface AiFormWrapperProps {
  enabled: boolean;
  customProperties: any;
  children: React.ReactNode;
}

export const FeedbackAiFormWrapper: FC<AiFormWrapperProps> = ({
  enabled,
  customProperties,
  children,
}) => {
  if (!enabled) {
    return <>{children}</>;
  }
  return (
    <ValidatedFormProvider
      inputWrapper={IDENTITY_WRAPPER}
      outputWrapper={aiAssistedOutputWrapper}
      customProperties={customProperties}
    >
      {children}
    </ValidatedFormProvider>
  );
};

// ---------------------------------------------------------------------------
// useFeedbackAiBannerInput – builds the input config item for the banner
// ---------------------------------------------------------------------------

interface BannerInputProps {
  hasNonEmptyDraftContent: boolean;
  handleAiBannerSubmit: (
    object: any,
    onCallback: any,
    handleClose: any,
    handleError: any
  ) => void;
  handleAiBannerCallback: (data: any) => void;
}

function useFeedbackAiBannerInput({
  hasNonEmptyDraftContent,
  handleAiBannerSubmit,
  handleAiBannerCallback,
}: BannerInputProps) {
  const { formatMessage } = useIntl();

  return useMemo(() => {
    const draftAuxDescriptors = hasNonEmptyDraftContent
      ? ENHANCE_DRAFT_DESCRIPTORS(formatMessage)
      : FIRST_DRAFT_DESCRIPTORS(formatMessage);
    return [
      {
        type: INPUT_TYPES.CUSTOM_INPUT,
        name: 'ai_draft_banner',
        hideLabel: true,
        component: (
          <FeedbackAiBanner
            hasNonEmptyDraftContent={hasNonEmptyDraftContent}
            draftAuxDescriptors={draftAuxDescriptors}
            handleAiBannerSubmit={handleAiBannerSubmit}
            handleAiBannerCallback={handleAiBannerCallback}
          />
        ),
      },
    ];
  }, [
    formatMessage,
    handleAiBannerCallback,
    handleAiBannerSubmit,
    hasNonEmptyDraftContent,
  ]);
}

// ---------------------------------------------------------------------------
// FeedbackAiBanner – the visual component
// ---------------------------------------------------------------------------

interface BannerProps {
  hasNonEmptyDraftContent: boolean;
  draftAuxDescriptors: any;
  handleAiBannerSubmit: (
    object: any,
    onCallback: any,
    handleClose: any,
    handleError: any
  ) => void;
  handleAiBannerCallback: (data: any) => void;
}

const FeedbackAiBanner: FC<BannerProps> = ({
  hasNonEmptyDraftContent,
  draftAuxDescriptors,
  handleAiBannerSubmit,
  handleAiBannerCallback,
}) => {
  const { formatMessage } = useIntl();

  return (
    <div className="alert alert-light py-3 mb-0 rounded-2 border">
      <Row className="align-items-center">
        <Col className="col-auto pe-0">
          <i className="fe fe-zap" />
        </Col>
        <Col>
          {hasNonEmptyDraftContent
            ? formatMessage({
                id: 'app.feedback.modal_feedback_editor_button.ai_banner.title_refine',
                defaultMessage: 'Enhance with AI, then make it your own.',
              })
            : formatMessage({
                id: 'app.feedback.modal_feedback_editor_button.ai_banner.title',
                defaultMessage: 'Draft with AI, then make it your own.',
              })}
        </Col>
        <Col className="col-auto">
          <InputAuxiliary
            name="ai_feedback_draft"
            value=""
            buttonClassName="btn btn-primary btn-sm"
            columnStyle="col-12"
            type="MODAL"
            {...draftAuxDescriptors}
            onSubmit={handleAiBannerSubmit}
            onCallback={handleAiBannerCallback}
            inputs={[
              {
                required: true,
                name: 'user_prompt',
                label: formatMessage({
                  id: 'app.feedback.modal_feedback_editor_button.ai_banner.prompt_label',
                  defaultMessage: 'AI prompt',
                }),
                helperText: formatMessage(
                  hasNonEmptyDraftContent
                    ? {
                        id: 'app.feedback.modal_feedback_editor_button.ai_banner.prompt_helper_refine',
                        defaultMessage:
                          'Describe how you want to refine or adjust the draft.',
                      }
                    : {
                        id: 'app.feedback.modal_feedback_editor_button.ai_banner.prompt_helper',
                        defaultMessage:
                          'Describe what you want the AI to draft.',
                      }
                ),
                placeholder: formatMessage(
                  hasNonEmptyDraftContent
                    ? {
                        id: 'app.feedback.modal_feedback_editor_button.ai_banner.prompt_placeholder_refine',
                        defaultMessage:
                          'e.g. Make it warmer and mention the Q4 project.',
                      }
                    : {
                        id: 'app.feedback.modal_feedback_editor_button.ai_banner.prompt_placeholder',
                        defaultMessage:
                          'e.g. Recognize their leadership during the product launch.',
                      }
                ),
                type: INPUT_TYPES.TEXTAREA,
                maxLength: 1000,
                minRows: 3,
                maxRows: 15,
              },
            ]}
          />
        </Col>
      </Row>
    </div>
  );
};

export default React.memo(FeedbackAiBanner);
